/**
 * Campaign Brand Safety aggregation.
 *
 * Two endpoints feed the campaign screen:
 * - `aggregateCampaignBrandSafety` maps `GET /campaign/brand-safety/{id}/creatorReports` (doc #2) — one
 *   per-creator SUMMARY card (`severityPerFlags`, `severityFlags`, `severityDist`, `topDetections`, `trademarkDetections`,
 *   `counts`, `overallSafetyScore`, `totalPostsScanned`/`totalPostsFlagged`, `creatorReportDetailsId`)
 *   plus campaign-level `status`/`updatedAt`/`totalCreators` — into the report-section view-model. Cards
 *   carry NO per-post data and NO per-run fields (no `checkId`/`status`); a card exists only for a
 *   creator's latest Succeeded check.
 * - `aggregateCampaignFlaggedPosts` maps `GET /campaign/brand-safety/{id}/flaggedPosts` (doc #4) — a
 *   campaign-wide `summary` plus per-creator groups of flagged posts (paginated by creator, and by each
 *   creator's own posts) — into the review-section view-model (Media view + Review tab).
 */

import { SEVERITY_BY_LEVEL, SEVERITY_ORDER } from '../utils/brandSafetyConstants';
import {
  bucketFlaggedHistogram,
  normalizeReviewPost,
  severitiesFromSeverityPerFlags,
} from '../utils/brandSafetyUtils';

/** The four hero status-chip states (Figma 805-3661), mapped from the campaign `status`. */
export const CAMPAIGN_STATUS = {
  NONE: 'none',
  RUNNING: 'running',
  COMPLETE: 'complete',
  FAILED: 'failed',
};

// Raw check statuses, shared with the slice's live `.checks.updated` reducer. Only the REST path needs
// normalizing — the websocket reader already lowercases.
const normalizeStatus = (status) =>
  String(status ?? '')
    .trim()
    .toLowerCase() || null;
export const RUNNING_STATES = ['queued', 'running', 'downloading_posts', 'processing'];
export const FAILED_STATES = ['failed', 'cancelled'];
// A check that reached a verdict. `partially_succeeded` is NOT terminal — that check keeps going.
export const TERMINAL_STATES = ['succeeded', 'failed', 'cancelled'];
export const isRunningCheckState = (status) => RUNNING_STATES.includes(normalizeStatus(status));
// In flight: anything short of a verdict, including an absent or unrecognized status.
export const isPendingCheckState = (status) => !TERMINAL_STATES.includes(normalizeStatus(status));

export function mapCampaignStatus(status) {
  const s = normalizeStatus(status);
  if (s === 'succeeded') return CAMPAIGN_STATUS.COMPLETE;
  if (FAILED_STATES.includes(s)) return CAMPAIGN_STATUS.FAILED;
  if (RUNNING_STATES.includes(s)) return CAMPAIGN_STATUS.RUNNING;
  // A partial check has results but is still working — the hero keeps its running chip.
  if (s === 'partially_succeeded') return CAMPAIGN_STATUS.RUNNING;
  return CAMPAIGN_STATUS.NONE;
}

// The per-creator check state, keyed by crd: the summary status overlaid with the live Reverb map
// (live wins). Shared by the hero's "running for …" subject and the campaign status derivation so the
// two never disagree. Reverb sends a crd and no name, and the summary may not have a row for it, so an
// entry can carry a status but no influencer.
function mergeCheckStates(report) {
  const creators = Array.isArray(report?.creators) ? report.creators : [];
  const byCrd = {};
  creators.forEach((creator, i) => {
    if (!creator) return;
    // Index key for the (unexpected) crd-less creator: never collides with a numeric crd from Reverb.
    const key = creator.crd != null ? String(creator.crd) : `i${i}`;
    byCrd[key] = { influencer: creator.influencer, status: creator.checkStatus };
  });
  const live = report?.liveChecks;
  if (live && typeof live === 'object') {
    Object.entries(live).forEach(([crd, status]) => {
      byCrd[crd] = { ...byCrd[crd], status };
    });
  }
  return Object.values(byCrd);
}

// Who the in-flight checks are for: the merged per-creator state. A check counts as in flight until it
// reports a verdict, so a row whose status is absent, unrecognized or `partially_succeeded` still names
// its creator. The summary may not be able to name a Reverb-only crd, so `count` can exceed `names.length`.
export function getRunningCreatorNames(report) {
  const names = [];
  let count = 0;
  mergeCheckStates(report).forEach(({ influencer, status }) => {
    if (!isPendingCheckState(status)) return;
    count += 1;
    const name = String(influencer?.name ?? '').trim();
    const username = String(influencer?.username ?? '').trim();
    if (name) names.push(name);
    else if (username) names.push(`@${username}`);
  });
  return { names, count };
}

/**
 * Campaign hero status from the FULL per-creator truth — the summary `checkStatus` overlaid with the
 * live Reverb map (same merge as `getRunningCreatorNames`): any check still in flight ⇒ RUNNING; else
 * any failed ⇒ FAILED; else at least one succeeded ⇒ COMPLETE; else NONE. Mirrors `mapCampaignStatus`
 * (incl. `partially_succeeded` → RUNNING) but over the whole per-creator union instead of one
 * campaign-level value. Used by the live `.checks.updated` reducer so a partial event (only the
 * creators that just settled) can't flip the hero to COMPLETE while others are still running.
 */
export function deriveCampaignStatus(report) {
  const statuses = mergeCheckStates(report).map((c) => c.status);
  if (!statuses.length) return CAMPAIGN_STATUS.NONE;
  if (statuses.some(isPendingCheckState)) return CAMPAIGN_STATUS.RUNNING;
  if (statuses.some((s) => FAILED_STATES.includes(normalizeStatus(s))))
    return CAMPAIGN_STATUS.FAILED;
  if (statuses.some((s) => normalizeStatus(s) === 'succeeded')) return CAMPAIGN_STATUS.COMPLETE;
  return CAMPAIGN_STATUS.NONE;
}

/**
 * LEGACY fallback only: `severityFlags` `{ L1..L4 }` → `{ severe, moderate, low, minor }` (L4→severe …
 * L1→minor). This is a POST-level histogram — each flagged post counted once at its top-level severity —
 * which disagrees with both the card's own Top Detections and the single report page's severity cards.
 * Prefer `toSeverityCountsFromPerFlags`; this is kept for older responses that predate `severityPerFlags`.
 */
function toSeverityCounts(severityFlags) {
  const counts = { severe: 0, moderate: 0, low: 0, minor: 0 };
  Object.entries(severityFlags || {}).forEach(([level, n]) => {
    const key = SEVERITY_BY_LEVEL[level];
    if (key) counts[key] = Number(n) || 0;
  });
  return counts;
}

/**
 * Per-creator severity distribution from `severityPerFlags` (per-category `{ L1..L4 }` histograms),
 * via the SAME helper the single report page feeds its `bs-severity-cards` from. WHY: the campaign card
 * and that creator's report page must show identical numbers, and they must agree with the card's own
 * Top Detections (also per-category). The old `severityFlags` source counted each flagged post once at
 * its top-level severity, so a post flagged L2/Trademark showed up as "minor" on the card while the
 * report page and Top Detections both called it L2 (AAPU-6775).
 *
 * Falls back to the post-level `severityFlags` histogram only when `severityPerFlags` is absent, not an
 * object, or empty — i.e. responses from before the BE started returning it.
 */
function toSeverityCountsFromPerFlags(severityPerFlags, severityFlags) {
  const hasPerFlags =
    severityPerFlags &&
    typeof severityPerFlags === 'object' &&
    Object.keys(severityPerFlags).length > 0;
  if (!hasPerFlags) return toSeverityCounts(severityFlags);
  const counts = { severe: 0, moderate: 0, low: 0, minor: 0 };
  severitiesFromSeverityPerFlags(severityPerFlags).forEach(({ key, objects }) => {
    if (key in counts) counts[key] = Number(objects) || 0;
  });
  return counts;
}

/** Highest-severity bucket with a non-zero count, else null. */
function worstSeverity(counts) {
  return SEVERITY_ORDER.find((key) => (counts[key] || 0) > 0) || null;
}

/** `topDetections` `{ category: level }` → `[{ label, severity }]` (tolerates the empty-array form). */
function toTopDetections(topDetections) {
  return Object.entries(topDetections || {}).map(([label, level]) => ({
    label,
    severity: SEVERITY_BY_LEVEL[level] || null,
  }));
}

/** `trademarkDetections` `{ brand: count }` → `{ brands: [top 2], matches: Σ }` (tolerates `[]`). */
function toTrademark(trademarkDetections) {
  const entries = Object.entries(trademarkDetections || {});
  return {
    brands: entries.map(([brand]) => brand).slice(0, 2),
    matches: entries.reduce((sum, [, n]) => sum + (Number(n) || 0), 0),
  };
}

/** Distinct top-detection categories across creators — the hero "flag tag" chips. */
function deriveTopCategories(creators, n = 3) {
  const seen = [];
  creators.forEach((creator) =>
    (creator.topDetections || []).forEach((d) => {
      if (d.label && !seen.includes(d.label)) seen.push(d.label);
    })
  );
  return seen.slice(0, n);
}

/**
 * @param {object} data  the `data` object from the creatorReports response (doc #2 — `{ status, updatedAt,
 *   totalCreators, totalChecks, creatorReports }`)
 * @param {string} [campaignName]
 * @returns campaign view-model consumed by the campaign screen
 */
export function aggregateCampaignBrandSafety(data = {}, campaignName = null) {
  const src = data && typeof data === 'object' ? data : {};
  const reports = (Array.isArray(src.creatorReports) ? src.creatorReports : []).filter(
    (c) => c && typeof c === 'object'
  );

  const creators = reports.map((report) => {
    const c = report.creator || {};
    const severityCounts = toSeverityCountsFromPerFlags(
      report.severityPerFlags,
      report.severityFlags
    );
    return {
      // The "View Full Report" deep link resolves off this.
      crd: report.creatorReportDetailsId ?? null,
      // doc #2 cards carry no per-run status, but a card exists ONLY for a creator's latest Succeeded check
      // — so treat it as succeeded for the hero's status/running-names derivation. Running/failed creators
      // have no card here; they only reach the hero via the live `.checks.updated` `liveChecks` map, which
      // the reducer overlays on top of this.
      checkStatus: 'succeeded',
      influencer: {
        name: c.name ?? null,
        username: c.username ?? null,
        platform: c.socialNetwork ?? null,
        avatarUrl: c.photoUrl ?? null,
        followers: c.followers ?? null,
        engagementRate: c.engagementRate ?? null,
        region: c.region ?? null,
      },
      safetyScore: report.overallSafetyScore ?? null,
      flaggedCount: Number(report.totalPostsFlagged) || 0,
      analyzedCount: Number(report.totalPostsScanned) || 0,
      severityCounts,
      worstSeverity: worstSeverity(severityCounts),
      topDetections: toTopDetections(report.topDetections),
      trademark: toTrademark(report.trademarkDetections),
      statusCounts: {
        approved: Number(report.counts?.approved) || 0,
        pending: Number(report.counts?.pending) || 0,
        rejected: Number(report.counts?.rejected) || 0,
      },
      posts: [],
    };
  });

  // Average only creators with a numeric score (unscored/failed checks report null).
  const scores = creators
    .map((creator) => creator.safetyScore)
    .filter((v) => v != null && Number.isFinite(Number(v)))
    .map(Number);

  const statusCounts = creators.reduce(
    (acc, creator) => ({
      approved: acc.approved + creator.statusCounts.approved,
      pending: acc.pending + creator.statusCounts.pending,
      rejected: acc.rejected + creator.statusCounts.rejected,
    }),
    { approved: 0, pending: 0, rejected: 0 }
  );

  return {
    campaignName,
    campaignScore: scores.length
      ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
      : null,
    totalFlagged: reports.reduce((s, c) => s + (Number(c.totalPostsFlagged) || 0), 0),
    analyzedCount: reports.reduce((s, c) => s + (Number(c.totalPostsScanned) || 0), 0),
    creatorCount: Number(src.totalCreators) || creators.length,
    status: mapCampaignStatus(src.status),
    analyzedAt: src.updatedAt ?? null,
    topCategories: deriveTopCategories(creators),
    statusCounts,
    // The Review badge base — total pending flagged posts across creators (lifetime, from the summary).
    reviewCount: statusCounts.pending,
    creators,
    // No per-post data in this endpoint; the Media table + Review list come from flaggedPosts (doc #4).
    media: [],
    posts: [],
  };
}

/** Distinct AI category labels flagged on one normalized review post. */
function postCategories(post) {
  const set = new Set();
  (post.objects || []).forEach((o) => o.category && set.add(o.category));
  (post.trademarks || []).forEach((t) => set.add(t.category || 'Trademark'));
  (post.segments || []).forEach((s) => s.category && set.add(s.category));
  return [...set];
}

/** Short, readable media name from a caption (the payload carries no filename). */
function captionName(caption, fallback) {
  const s = String(caption || '').trim();
  if (!s) return fallback;
  return s.length > 48 ? `${s.slice(0, 48).trim()}…` : s;
}

/**
 * One normalized review post → a Media-table row. Shared by the campaign Media view. Each row carries its
 * creator's `creatorReportDetailsId` (`crd`) so a bulk resolve from the table can target the right creator
 * directly, without a username→crd join.
 */
function toMediaRow(normalized, raw, creator, crd) {
  return {
    // `key` (= postId) is what CustomTable matches row selection on (it keys selection on item.key).
    key: normalized.id,
    postId: normalized.id,
    creatorReportDetailsId: crd,
    creatorUsername: creator.username ?? null,
    creatorName: creator.name ?? null,
    creatorAvatar: creator.photoUrl ?? null,
    // TODO(BE): no filename in the payload — derived from the caption.
    name: captionName(raw.caption, `${creator.username || 'creator'} ${normalized.type}`),
    type: normalized.type,
    thumbnailUrl: normalized.thumbnailUrl,
    mediaUrl: normalized.mediaUrl ?? null,
    severity: normalized.severity,
    status: normalized.status,
    categories: postCategories(normalized),
    flagCount: normalized.issueCount,
    // TODO(BE): reviewedAt only exists for reviewed posts; views absent entirely.
    reviewedAt: raw.review?.reviewedAt ?? null,
    views: null,
  };
}

/**
 * Map the campaign flaggedPosts endpoint (doc #4) into the review view-model: a campaign-wide `summary`,
 * cursor `pagination`, and a FLAT feed of flagged posts (not grouped by creator — endpoint 2's cards
 * already cover the per-creator view). The doc #4 post IS "the flagged post entry", so
 * `normalizeReviewPost` consumes it directly — no thumbnail-swap hack, since the entry carries a real
 * `thumbnailUrl` and a playable `mediaUrl`. Each post carries its own `creatorReportDetailsId`/`creator`,
 * so no username→crd join is needed for the review write endpoints.
 *
 * @param {object} data  the `data` object from the doc #4 response
 * @returns {{ summary: object|null, pagination: object, posts: Array, media: Array }}
 */
export function aggregateCampaignFlaggedPosts(data = {}) {
  const src = data && typeof data === 'object' ? data : {};
  const rawPosts = Array.isArray(src.posts) ? src.posts : [];

  const posts = [];
  const media = [];
  rawPosts.forEach((raw) => {
    if (!raw || typeof raw !== 'object') return;
    const normalized = normalizeReviewPost(raw);
    if (!normalized) return;
    const crd = raw.creatorReportDetailsId ?? null;
    const creator = raw.creator || {};
    posts.push({
      ...normalized,
      creatorReportDetailsId: crd,
      creatorUsername: creator.username ?? null,
    });
    media.push(toMediaRow(normalized, raw, creator, crd));
  });

  const pg = src.pagination && typeof src.pagination === 'object' ? src.pagination : {};
  const summary = src.summary && typeof src.summary === 'object' ? src.summary : null;
  return {
    summary: src.summary ?? null,
    // Campaign-wide publish-date timeline, aggregated across every influencer by the BE. Bucketed straight
    // from the flagged day histogram (flagged-only, every bucket reads flagged) so a campaign's thousands of
    // flagged posts aren't expanded into per-post points; the chart consumes these buckets directly via its
    // `dateBuckets` prop. `analyzedCount` drives the axis total and `firstAnalyzedPostPublishDate` the start.
    timelineBuckets: bucketFlaggedHistogram(summary?.flaggedPostsByPublishDate),
    analyzedCount: Number(summary?.totalPostsScanned) || 0,
    firstAnalyzedPostPublishDate: summary?.firstAnalyzedPostPublishDate ?? null,
    // Cursor pagination — `nextCursor` (opaque resume token) drives "Load more"; `total` is the campaign's
    // lifetime flagged count (== summary.totalPostsFlagged), for the "N of M" hint.
    pagination: {
      total: Number.isFinite(Number(pg.total)) ? Number(pg.total) : posts.length,
      limit: pg.limit != null ? Number(pg.limit) : null,
      nextCursor: pg.nextCursor ?? null,
      hasMore: Boolean(pg.hasMorePages),
    },
    posts,
    media,
  };
}

export default aggregateCampaignBrandSafety;
